import PropTypes from 'prop-types';
import React from 'react';
import { injectIntl } from 'react-intl';
import styled, { css } from 'styled-components';
import { grey, grey25 } from '@zocdoc/branding/colors';
import { breakpoint, Breakpoints } from '@zocdoc/components/src/grid';
import { Arrow } from '@zocdoc/components/src/icons';
import { sharpSansSemibold } from '@zocdoc/components/src/typography';

import { LOCALIZED_STRINGS } from './constants';
import { Overlay } from '../../../Shared/Overlay';
import { InteractionLabels } from '../../../middleware/google-analytics';

export class PictureGalleryView extends React.PureComponent {
    static propTypes = {
        visible: PropTypes.bool,
        current: PropTypes.number,
        pictures: PropTypes.arrayOf(PropTypes.string),
    };

    componentDidMount() {
        this.props.galleryShown();
    }

    render() {
        const {
            intl,
            pictures,
            current = 0,
            name,
        } = this.props;

        const thumbnails = pictures.map(this.renderThumbnail);
        const currentPicture = pictures[current] ? pictures[current].replace(/small\.jpg$/, 'zoom.jpg') : '';

        const currentValueOfMaxValue = intl.formatMessage(LOCALIZED_STRINGS.CurrentValueOfMaxValue, {
            value: current + 1,
            maxValue: thumbnails.length,
        });

        return (
            <Overlay
                data-test="gallery-container"
                onClickClose={this.close}
            >
                <PictureGalleryContent>
                    <nav>
                        <PictureGalleryPaginator>
                            <PictureGalleryLeftArrow
                                aria-label={intl.formatMessage(LOCALIZED_STRINGS.Previous)}
                                data-test="profile-picture-gallery-left-arrow"
                                onClick={this.previous}
                                role="button"
                                tabIndex="0"
                            >
                                <PictureGalleryArrow
                                    color={grey25}
                                    direction="left"
                                    height={18}
                                />
                            </PictureGalleryLeftArrow>

                            <PictureGalleryControlText>
                                {currentValueOfMaxValue}
                            </PictureGalleryControlText>

                            <PictureGalleryRightArrow
                                aria-label={intl.formatMessage(LOCALIZED_STRINGS.Next)}
                                data-test="profile-picture-gallery-right-arrow"
                                onClick={this.next}
                                role="button"
                                tabIndex="0"
                            >
                                <PictureGalleryArrow
                                    color={grey25}
                                    direction="right"
                                    height={18}
                                />
                            </PictureGalleryRightArrow>
                        </PictureGalleryPaginator>
                    </nav>

                    <PictureGalleryZoomedImage
                        alt={name}
                        data-test="profile-picture-gallery-zoom"
                        src={currentPicture}
                    />

                    <PictureGalleryThumbnails count={thumbnails.length >= 12 ? 12 : thumbnails.length}>
                        {thumbnails}
                    </PictureGalleryThumbnails>
                </PictureGalleryContent>
            </Overlay>
        );
    }

    renderThumbnail = (img, idx) => {
        const {
            intl,
            firstName,
            lastName,
            pictures,
            current = 0,
        } = this.props;

        const alt = intl.formatMessage(LOCALIZED_STRINGS.ThumbnailAlt, {
            firstName,
            lastName,
            number: idx + 1,
            count: pictures.length,
        });

        return (
            <PictureGalleryThumbnailImage
                key={idx}
                active={current === idx}
                alt={alt}
                data-index={idx}
                data-test="profile-picture-gallery-thumbnail"
                onClick={this.select}
                role="button"
                src={img}
                tabIndex="0"
            />
        );
    };

    previous = () => {
        const {
            current,
            pictures,
            previousPicture,
        } = this.props;
        const picture = ((current - 1) + pictures.length) % pictures.length;
        previousPicture({
            picture,
            pictureUrl: pictures[picture],
            interaction: InteractionLabels.Click,
        });
    };

    next = () => {
        const {
            current,
            pictures,
            nextPicture,
        } = this.props;
        const picture = ((current + 1) + pictures.length) % pictures.length;
        nextPicture({
            picture,
            pictureUrl: pictures[picture],
            interaction: InteractionLabels.Click,
        });
    };

    close = () => this.props.closeGallery({ interaction: InteractionLabels.Click });

    select = ev => {
        const {
            current,
            selectPicture,
            pictures,
        } = this.props;

        ev.preventDefault();

        const idx = parseInt(ev.currentTarget.getAttribute('data-index'), 10);
        if (idx !== current) {
            selectPicture({
                picture: idx,
                pictureUrl: pictures[idx],
                interaction: InteractionLabels.Click,
            });
        }
    };
}

const GALLERY_CONTROL_SIZE = 28;
const GALLERY_MARGIN = 16;
const GALLERY_THUMBNAIL_SIZE = 80;

const PictureGalleryContent = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    z-index: 12;
`;

const PictureGalleryPaginator = styled.div`
    padding: ${GALLERY_MARGIN}px 0;
    text-align: center;
    width: 100%;
`;

const PictureGalleryControl = styled.div`
    cursor: pointer;
    display: inline-block;
    margin: ${GALLERY_MARGIN}px;
    min-height: ${GALLERY_CONTROL_SIZE}px;
    min-width: ${GALLERY_CONTROL_SIZE}px;
    vertical-align: middle;
`;

const pictureGalleryArrowCss = css`
    border-radius: 50%;

    ${breakpoint.medium} {
        &:hover {
            background: ${grey};
        }
    }
`;

const PictureGalleryArrow = styled(Arrow)`
    color: ${grey25};
    font-size: 16px;
    margin-top: 6px;
`;

const PictureGalleryLeftArrow = styled(PictureGalleryControl)`
    ${pictureGalleryArrowCss}

    ${PictureGalleryArrow} {
        margin-right: 2px;
    }
`;

const PictureGalleryRightArrow = styled(PictureGalleryControl)`
    ${pictureGalleryArrowCss}

    ${PictureGalleryArrow} {
        margin-left: 2px;
    }
`;

const PictureGalleryControlText = styled.div`
    ${sharpSansSemibold(13, GALLERY_CONTROL_SIZE)}
    color: #FFF;
    display: inline-block;
    letter-spacing: 0.05em;
    margin: 0 12px;
`;

const PictureGalleryZoomedImage = styled.img`
    display: block;
    margin-left: auto;
    margin-right: auto;

    /* Leave space for the controls */
    max-height: calc(100vh - #{$gallery-margin * 5 + $gallery-control-height + 3});
    max-width: 100%;

    /* Leave space for the thumbnails, but only if we're on a large enough screen */
    @media screen and (min-width: ${Breakpoints.xsmall}px) and (min-height: 400px) {
        max-height: calc(100vh - ${(GALLERY_MARGIN * 7) + GALLERY_CONTROL_SIZE + 3 + GALLERY_THUMBNAIL_SIZE}px);
    }
`;

const PictureGalleryThumbnailImage = styled.img`
    cursor: pointer;
    margin: ${GALLERY_MARGIN / 2}px;
    opacity: ${p => p.active ? 1 : 0.6};
    height: ${GALLERY_THUMBNAIL_SIZE / 2}px;
    width: ${GALLERY_THUMBNAIL_SIZE / 2}px;
`;

const PictureGalleryThumbnails = styled.div`
    display: none;
    max-height: ${(GALLERY_MARGIN * 2) + GALLERY_THUMBNAIL_SIZE}px;
    overflow-y: scroll;
    text-align: center;
    width: 100%;

    /* Show thumbnails only if the screen is large enough */
    @media screen and (min-width: ${Breakpoints.xsmall}px) and (min-height: 400px) {
        bottom: 0;
        display: block;
        margin: ${GALLERY_MARGIN / 2}px 0;
        position: fixed;
    }

    ${p => p.count && css`
        @media screen and (min-width: ${(p.count * (GALLERY_THUMBNAIL_SIZE + (GALLERY_MARGIN * 2))) + (2 * GALLERY_THUMBNAIL_SIZE)}px) {
            ${PictureGalleryThumbnailImage} {
                margin: ${GALLERY_MARGIN}px;
                height: ${GALLERY_THUMBNAIL_SIZE}px;
                width: ${GALLERY_THUMBNAIL_SIZE}px;
            }
        }
    `}
`;

export default injectIntl(PictureGalleryView);
